/**
 * Phase 3e — Admin self-signup gated by invite code.
 *
 * Bootstrap-only path. Founding admin (or anyone with the invite code)
 * provisions their own laguna_admins row + password without needing a
 * second admin to issue a temp.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { adminSignup } from "../lib/admin";
import { track } from "../lib/analytics";
import { isValidEmail } from "../lib/dataHygiene";

import "./Admin.css";

const ERR_COPY = {
    invalid_invite_code: "That invite code isn't valid.",
    already_registered: "An admin already exists for that email. Sign in instead.",
    invalid_email: "That email doesn't look right — mind checking it?",
    weak_password: "Password must be at least 8 characters.",
    rate_limited: "Too many attempts from this network. Try again in an hour.",
    network: "Couldn't reach the server. Try again in a moment.",
};

export default function AdminSignup() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.title = "Laguna · admin sign-up";
        track("admin_signup_view", {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password || !inviteCode || submitting) return;
        if (!isValidEmail(email.trim())) {
            setErr("invalid_email");
            return;
        }
        if (password.length < 8) {
            setErr("weak_password");
            return;
        }
        setSubmitting(true);
        setErr(null);
        try {
            const res = await adminSignup({
                email: email.trim(),
                password,
                invite_code: inviteCode.trim(),
            });
            track("admin_signup_ok", {});
            // The backend already pre-fills the redirect; honor it.
            navigate(res.redirect_to || "/admin/signin?just_registered=1");
        } catch (e2) {
            const status = e2?.response?.status;
            const detail = e2?.response?.data?.detail;
            if (status === 403 && detail === "invalid_invite_code") {
                setErr("invalid_invite_code");
            } else if (status === 409 && detail === "already_registered") {
                setErr("already_registered");
            } else if (status === 429) {
                setErr("rate_limited");
            } else {
                setErr("network");
            }
            track("admin_signup_fail", { status, detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-page" data-testid="admin-signup">
            <div className="admin-auth-wrap">
                <div className="admin-auth-card">
                    <div className="admin-auth-eyebrow mono">
                        LAGUNA · ADMIN
                    </div>
                    <h1 className="admin-auth-h1">Create admin account</h1>
                    <p className="admin-auth-sub">
                        Founder bootstrap. Provide your email, choose a
                        password, and enter the invite code shared with you
                        out-of-band.
                    </p>
                    <form onSubmit={submit}>
                        <label className="admin-auth-label">
                            Email
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                autoFocus
                                data-testid="admin-signup-email"
                                disabled={submitting}
                            />
                        </label>
                        <label className="admin-auth-label">
                            Password
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                data-testid="admin-signup-password"
                                disabled={submitting}
                            />
                        </label>
                        <label className="admin-auth-label">
                            Invite code
                            <input
                                type="text"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value)}
                                autoComplete="off"
                                spellCheck={false}
                                data-testid="admin-signup-invite-code"
                                disabled={submitting}
                            />
                        </label>
                        {err && (
                            <div
                                className="admin-auth-error"
                                data-testid="admin-signup-error"
                            >
                                {ERR_COPY[err] || ERR_COPY.network}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="admin-btn primary admin-auth-submit"
                            disabled={
                                submitting || !email || !password || !inviteCode
                            }
                            data-testid="admin-signup-submit"
                        >
                            {submitting ? "Creating account…" : "Create account"}
                        </button>
                    </form>
                    <p
                        className="admin-auth-sub"
                        style={{ marginTop: "1.25rem", fontSize: "0.85rem" }}
                    >
                        Already have an account?{" "}
                        <Link to="/admin/signin" data-testid="admin-signup-to-signin">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
